/**
 * Intercepteur fetch global pour gérer les erreurs d'autorisation de l'API :
 *  - 401 (session expirée/invalide) -> /session-expiree, après purge de la session
 *  - 403 (droits insuffisants)      -> /acces-refuse, sans purger la session
 * Aucune redirection si l'on est déjà sur une page publique ou une page d'état.
 */

let isRedirecting = false;

// Pages où on ne doit PAS rediriger (publiques + pages d'état, pour éviter les boucles)
const publicPaths = [
  "/login",
  "/register",
  "/reset-password",
  "/forgot-password",
  "/verify-email",
  "/publications",
  "/session-expiree",
  "/acces-refuse",
  "/maintenance",
];

/**
 * Vérifie si le chemin actuel est une page publique
 */
const isPublicPath = (): boolean => {
  const currentPath = window.location.pathname;
  // Accueil : page publique (annuaire consultable en modale sans connexion). Match EXACT,
  // car un startsWith("/") rendrait toutes les pages "publiques" et desactiverait la redirection.
  if (currentPath === "/") return true;
  return publicPaths.some((path) => currentPath.startsWith(path));
};

/**
 * Wrapper autour de fetch pour intercepter les réponses 401
 * et rediriger automatiquement vers la page de login
 */
export const setupFetchInterceptor = () => {
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);

      // 401 Unauthorized : session expirée/invalide -> page "Connexion requise" (purge la session).
      if (response.status === 401 && !isRedirecting && !isPublicPath()) {
        isRedirecting = true;

        localStorage.removeItem("user");
        localStorage.removeItem("token");

        console.warn("Session expirée ou invalide. Redirection vers /session-expiree...");
        window.location.href = "/session-expiree";

        setTimeout(() => {
          isRedirecting = false;
        }, 1000);
      }

      // 403 Forbidden : authentifié mais droits insuffisants -> page "Accès refusé".
      // On NE purge PAS la session : l'utilisateur reste connecté, c'est juste cette action
      // qui lui est interdite.
      // if (response.status === 403 && !isRedirecting && !isPublicPath()) {
      //   isRedirecting = true;

      //   console.warn("Accès refusé (403). Redirection vers /acces-refuse...");
      //   window.location.href = "/acces-refuse";

      //   setTimeout(() => {
      //     isRedirecting = false;
      //   }, 1000);
      // }

      return response;
    } catch (error) {
      throw error;
    }
  };
};

/**
 * Helper pour faire des requêtes avec gestion automatique des erreurs d'authentification
 * @param url URL de la requête
 * @param options Options fetch standard
 * @returns Promise<Response>
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  const token = localStorage.getItem("token");

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Si 401, l'intercepteur global gérera la redirection
  if (response.status === 401) {
    throw new Error("Unauthorized - Token expired or invalid");
  }

  return response;
};
